import { useEffect, useState } from "react";
import { Upload, Trash2, Search, Copy, Loader2, Image as ImageIcon, Video } from "lucide-react";
import { toast } from "sonner";
import { listMedia, uploadMedia, deleteMedia } from "@/lib/adminCmsApi";
import AdminShell from "@/components/admin/AdminShell";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try { setItems(await listMedia({ resource_type: typeFilter || undefined, q: filter || undefined })); }
    catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [typeFilter]);

  const onUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        await uploadMedia(f, { folder: "ags/library" });
      }
      toast.success(`Uploaded ${files.length} file(s)`);
      load();
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete "${item.original_filename}"? Removes from Cloudinary too.`)) return;
    try { await deleteMedia(item.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copied");
  };

  return (
    <AdminShell title="Media Library">
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <div className="flex-1 flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ags-text-muted" />
            <input value={filter} onChange={(e) => setFilter(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                   placeholder="Search…" className="bg-white border border-[#0E2A57]/15 pl-9 pr-3 py-2.5 w-60 focus:border-[#0E2A57] focus:outline-none" />
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  className="bg-white border border-[#0E2A57]/15 px-3 py-2.5 focus:border-[#0E2A57] focus:outline-none">
            <option value="">All types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
          </select>
        </div>
        <label className="cursor-pointer inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} />
        </label>
      </div>

      <div className="bg-white border border-[#0E2A57]/15 p-4">
        {loading ? <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin" /></div> :
         items.length === 0 ? <div className="p-12 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-ags-text-muted">No media yet — upload above</div> :
         <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
           {items.map((m) => (
             <div key={m.id} data-testid={`media-${m.id}`} className="bg-white border border-ags-hairline flex flex-col group">
               <div className="aspect-square bg-ags-cream flex items-center justify-center relative overflow-hidden">
                 {m.resource_type === "video" ? (
                   <video src={m.url} className="w-full h-full object-cover" muted />
                 ) : (
                   <img src={m.url} alt={m.alt_text || m.original_filename} className="w-full h-full object-cover" />
                 )}
                 <div className="absolute top-1 right-1 bg-[#050B14]/80 text-white px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] flex items-center gap-1">
                   {m.resource_type === "video" ? <Video size={9} /> : <ImageIcon size={9} />}
                   {m.format || m.resource_type}
                 </div>
               </div>
               <div className="p-2.5 flex-1 flex flex-col">
                 <div className="text-xs text-ags-ink truncate" title={m.original_filename}>{m.original_filename}</div>
                 <div className="font-mono text-[10px] text-ags-text-muted mt-0.5">{(m.bytes/1024).toFixed(0)} KB{m.width ? ` · ${m.width}×${m.height}` : ""}</div>
                 <div className="mt-2 flex items-center gap-1">
                   <button onClick={() => copy(m.url)} className="flex-1 inline-flex items-center justify-center gap-1 bg-ags-cream hover:bg-[#BEE7F5] py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[#0E2A57]">
                     <Copy size={10} /> URL
                   </button>
                   <button onClick={() => onDelete(m)} className="text-red-600 hover:bg-red-50 p-1.5"><Trash2 size={12} /></button>
                 </div>
               </div>
             </div>
           ))}
         </div>
        }
      </div>
    </AdminShell>
  );
}
